/* eslint-disable max-len */
import { all, call, put, select, take, takeLatest } from "redux-saga/effects";
import { merge } from "lodash";

import {
  KycTransactionSuccess,
  CREATE_KYC_TRANSACTION,
  ANALYSE_OCR,
  analyseOcrSuccessEid,
  analyseOcrSuccessPassport,
  analyseOcrFail,
  CREATE_FACE_SCAN_KEY,
  CHECK_FACE_LIVELINESS,
  saveFaceLivelinessFeedback,
  createFaceScanKeySuccess,
  SET_LIVELINESS_DATA,
  validateIdentitySuccess,
  validateIdentityFail,
  validateEntityConfirmSuccess,
  validateEntityConfirmFail,
  saveFaceLivelinessFeedbackError,
  notifyHostSuccess,
  notifyHostError,
  NOTIFY_HOST,
  loadEidDocuments,
  loadPassportDocuments,
  loadConfirmEntity,
  GET_KYC_STATUS,
  getKycSuccess,
  getKycError,
  analyseOcrAgeRestriction,
  setLoading,
  SEND_EFR_INVITE,
  sendEFRInviteSuccess,
  sendEFRInviteError,
  setDocClarityError
} from "../actions/kyc";
import {
  getAuthorizationHeader,
  getDatalist,
  getIsMultiPartnerJourney,
  getOrganizationInfo,
  getProspectId,
  getSignatories,
  getApplicantInfo,
  getDocuments
} from "../selectors/appConfig";
import {
  analyzeOcrData,
  createKYCTransaction,
  getOCRDataStatus,
  sendEFRInvite
} from "../../api/apiClient";
import { getKyc, getLivelinessData, getTransactionId } from "../selectors/kyc";
import { log } from "../../utils/loggger";
import {
  DOC_TYPE_EID,
  DOC_TYPE_PASSPORT,
  EID_EXPIRY,
  ERROR_MESSAGES,
  DOC_MISMATCH,
  PASSPORT_EXPIRY,
  INVALID_DOCUMENT,
  screeningStatus,
  EFR_CHECK_ERROR,
  APP_STOP_SCREEN_RESULT,
  AGE_RESTRICTION,
  NEXT,
  AUTO,
  CHECKSUM_FAILED_MESSAGE,
  EFR_CHECK
} from "../../constants";
import routes from "../../routes";
import {
  checkDocumentValid,
  checkIsDocTypeEid,
  checkIsDocTypePassport,
  getOcrFieldValueBySource
} from "../../utils/ocr";
import { NotificationsManager } from "../../components/Notification";
import {
  resetFormStep,
  sendProspectToAPI,
  SEND_PROSPECT_TO_API_FAIL,
  SEND_PROSPECT_TO_API_SUCCESS,
  setScreeningError
} from "../actions/sendProspectToAPI";
import { updateProspect } from "../actions/appConfig";
import { getAgentId, getRoCode } from "../selectors/loginSelector";
import { formattedAccTimeStamp } from "../../utils/getAcceptedTimeStamp/getAcceptedTimeStamp";
import { setScreeningResults } from "./sendProspectToAPI";
import {
  checkIsSelectedStakeholderSignatory,
  getSelectedSignatoryIndex,
  getSelectedStakeholderId
} from "../selectors/stakeholders";
import { getwtmSessionDetails } from "../selectors/webToMobile";

export const defaultSignatoryId = "SID1";
export const individualTypeValues = {
  SHAREHOLDER: "Shareholder",
  SIGNATORY: "Signatory"
};

const getIndividualType = (isMultiPartnerJourney, isShareHolderASignatory) => {
  if (!isMultiPartnerJourney) {
    return individualTypeValues.SIGNATORY;
  }

  return isShareHolderASignatory
    ? individualTypeValues.SIGNATORY
    : individualTypeValues.SHAREHOLDER;
};

export function* createKycTransactionSaga() {
  try {
    const headers = yield select(getAuthorizationHeader);
    const prospectId = yield select(getProspectId);
    const signatoryId = yield select(getSelectedStakeholderId);
    const isMulitPartnerJourney = yield select(getIsMultiPartnerJourney);
    const isShareHolderASignatory = yield select(checkIsSelectedStakeholderSignatory);
    const individualId = signatoryId ? signatoryId : defaultSignatoryId;
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);
    const wtm = !!webMobileRefId;
    const individualType = getIndividualType(isMulitPartnerJourney, isShareHolderASignatory);
    const reuseExistingTransaction = true;
    const isMultipartner = yield select(getIsMultiPartnerJourney);
    yield put(setLoading(true));
    const response = yield call(
      createKYCTransaction.send,
      prospectId,
      individualId,
      reuseExistingTransaction,
      isMultipartner,
      individualType,
      headers,
      wtm
    );
    yield put(KycTransactionSuccess(response.data));
    yield call(getCurrentKYCStatus);
  } catch (error) {
    log(error);
  }
}

function* validateOCR(response, documentType, analysedEidData) {
  const { showDocClarityError } = yield select(getKyc);
  const daysToExpiry = getOcrFieldValueBySource(response?.daysToExpiry);
  const nationalityIso2 = getOcrFieldValueBySource(response?.nationalityIso2);
  const nationalityIso3 = getOcrFieldValueBySource(response?.nationalityIso3);
  const firstName = getOcrFieldValueBySource(response?.firstName);
  const fullName = getOcrFieldValueBySource(response?.fullName);
  const age = getOcrFieldValueBySource(response?.age);
  const passportDataFailureCount = showDocClarityError.count;
  const isShareHolderASignatory = yield select(checkIsSelectedStakeholderSignatory);
  const isMulitPartnerJourney = yield select(getIsMultiPartnerJourney);

  if (documentType === DOC_TYPE_EID) {
    if (!checkIsDocTypeEid(response)) {
      yield put(analyseOcrFail(INVALID_DOCUMENT));
      return false;
    }
    if (daysToExpiry <= 10) {
      yield put(analyseOcrFail(EID_EXPIRY));
    } else {
      if (age < 18 && (isShareHolderASignatory || !isMulitPartnerJourney)) {
        yield put(analyseOcrAgeRestriction(AGE_RESTRICTION));
        return false;
      }
      yield put(analyseOcrSuccessEid(response));
      return true;
    }

    return false;
  }
  if (documentType === DOC_TYPE_PASSPORT) {
    if (!checkIsDocTypePassport(response)) {
      yield put(analyseOcrFail(INVALID_DOCUMENT));
      return;
    }
    const nationalityAsInEidIso2 = getOcrFieldValueBySource(analysedEidData?.nationalityIso2);
    const nationalityAsInEidIso3 = getOcrFieldValueBySource(analysedEidData?.nationalityIso3);
    if (
      firstName === "" ||
      (firstName && firstName.length > 30) ||
      fullName === "" ||
      nationalityIso2 === ""
    ) {
      if (passportDataFailureCount === 3) {
        yield call(sendProspectToApiWithScreeningResults, EFR_CHECK);
      } else {
        yield put(
          setDocClarityError({
            message: CHECKSUM_FAILED_MESSAGE,
            count: passportDataFailureCount + 1
          })
        );
        yield put(analyseOcrFail(CHECKSUM_FAILED_MESSAGE));
      }
    } else if (
      nationalityAsInEidIso2 !== nationalityIso2 &&
      nationalityIso3 !== nationalityAsInEidIso3
    ) {
      yield put(analyseOcrFail(DOC_MISMATCH));
    } else if (daysToExpiry <= 10) {
      yield put(analyseOcrFail(PASSPORT_EXPIRY));
    } else {
      yield put(analyseOcrSuccessPassport(response));
      return true;
    }
    return false;
  }
}

export function* analyseOcrDataSaga({ payload }) {
  const { ocrData, documentType } = payload;
  try {
    const { KycTransactionResponse, analysedEidData } = yield select(getKyc);
    const headers = yield select(getAuthorizationHeader);

    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);

    const response = yield call(
      analyzeOcrData.send,
      KycTransactionResponse.transactionId,
      ocrData,
      headers,
      documentType,
      webMobileRefId
    );
    const isDocumentValid = documentType === DOC_TYPE_EID ? checkDocumentValid(response) : true;

    if (!response || !isDocumentValid) {
      yield put(analyseOcrFail(INVALID_DOCUMENT));
      return;
    }

    yield call(validateOCR, response, documentType, analysedEidData);
  } catch (error) {
    const notificationOptions = {};

    const message =
      ERROR_MESSAGES[(error?.response?.data?.errorCode)] &&
      ERROR_MESSAGES[(error?.response?.data?.errorCode)];

    if (message) {
      yield put(analyseOcrFail(message));
    } else {
      NotificationsManager.add(notificationOptions);
      yield put(analyseOcrFail());
    }
  }
}

export function* createFaceScanSaga() {
  try {
    const headers = yield select(getAuthorizationHeader);
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);

    const response = yield call(analyzeOcrData.getFaceScanKey, headers, webMobileRefId);
    yield put(createFaceScanKeySuccess(response));
  } catch (error) {
    log(error);
  }
}

export function* checkFaceLiveliness({ payload }) {
  try {
    const transactionId = yield select(getTransactionId);
    let headers = yield select(getAuthorizationHeader);
    headers = { headers: { ...headers.headers, "Content-Type": "text/plain" } };
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);
    const response = yield call(
      analyzeOcrData.postFaceLiveliness,
      transactionId,
      payload,
      headers,
      webMobileRefId
    );
    yield put(saveFaceLivelinessFeedback(response.data));
  } catch (error) {
    const notificationOptions = { title: "Oops", message: error.message };
    NotificationsManager.add(notificationOptions);
    yield put(saveFaceLivelinessFeedbackError());
    log(error);
  }
}

export function* notifyHost() {
  try {
    const transactionId = yield select(getTransactionId);
    const headers = yield select(getAuthorizationHeader);

    const signatoryDetails = yield select(getSignatories);
    const signatoryId = yield select(getSelectedStakeholderId);
    const currentStateSignatoryIndex = yield select(getSelectedSignatoryIndex);
    const documentList = yield select(getDocuments);
    const applicantInfo = yield select(getApplicantInfo);
    const { maritalStatus: maritalStatusList } = yield select(getDatalist);
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);

    const notifyHostResponse = yield call(
      analyzeOcrData.notifyHost,
      transactionId,
      signatoryId,
      headers,
      webMobileRefId
    );
    const prospectStakeholdersDocuments = documentList?.stakeholdersDocuments || {};
    // remove keys with null vlaues from prospectStakeholderDocuments
    const existingDocuments = Object.keys(prospectStakeholdersDocuments).reduce((acc, key) => {
      if (prospectStakeholdersDocuments[key] !== null) {
        acc[key] = prospectStakeholdersDocuments[key];
      }
      return acc;
    }, {});

    const { preScreening } = notifyHostResponse;
    const isScreeningError = preScreening && preScreening.statusOverAll === APP_STOP_SCREEN_RESULT;

    const individualId = signatoryId ? signatoryId : defaultSignatoryId;

    if (isScreeningError) {
      yield call(setScreeningResults, notifyHostResponse);
    } else if (notifyHostResponse) {
      yield put(notifyHostSuccess(notifyHostResponse));
      const {
        signatoryInfo,
        documents: { stakeholdersDocuments }
      } = notifyHostResponse;

      const notifyResponseCurrentSignatoryIndex = signatoryInfo.findIndex(
        eachItem => eachItem.signatoryId === individualId
      );

      if (
        signatoryInfo[notifyResponseCurrentSignatoryIndex].fullName !==
        signatoryDetails[currentStateSignatoryIndex]?.fullName
      ) {
        signatoryDetails[currentStateSignatoryIndex].editedFullName =
          signatoryInfo[notifyResponseCurrentSignatoryIndex].fullName;
      } else {
        signatoryInfo[notifyResponseCurrentSignatoryIndex].editedFullName =
          signatoryDetails[currentStateSignatoryIndex]?.editedFullName ||
          signatoryInfo[notifyResponseCurrentSignatoryIndex].fullName;
      }
      const maritalStatus = signatoryInfo[notifyResponseCurrentSignatoryIndex]?.maritalStatus;

      const mappedMaritalStatus =
        maritalStatusList &&
        maritalStatusList.filter(
          status => status.displayText.toLowerCase() === maritalStatus?.trim().toLowerCase()
        );
      // const signatory = merge(signatoryDetails, signatoryInfo);
      const updateSignatoryInfo = signatoryDetails.map(item => {
        if (item.signatoryId === individualId) {
          return merge(item, signatoryInfo[notifyResponseCurrentSignatoryIndex]);
        }
        return item;
      });

      const editedCompanyEmail = signatoryDetails[currentStateSignatoryIndex]?.editedEmailId;
      const editedCompanyPhone = signatoryDetails[currentStateSignatoryIndex]?.editedMobileNumber;
      const editedStakeholderEmail =
        signatoryDetails[currentStateSignatoryIndex]?.contactDetails?.editedPrimaryEmailId;
      const editedStakeholderPhone =
        signatoryDetails[currentStateSignatoryIndex]?.contactDetails?.editedPrimaryMobileNo;
      //Name on card for account info screen
      yield put(
        updateProspect({
          "prospect.signatoryInfo": updateSignatoryInfo,
          "prospect.documents.stakeholdersDocuments": {
            ...existingDocuments,
            ...stakeholdersDocuments
          },
          [`prospect.signatoryInfo[${currentStateSignatoryIndex}].maritalStatus`]:
            mappedMaritalStatus && mappedMaritalStatus[0]?.code,
          [`prospect.signatoryInfo[${currentStateSignatoryIndex}].editedEmailId`]:
            editedCompanyEmail || applicantInfo.email,
          [`prospect.signatoryInfo[${currentStateSignatoryIndex}].editedMobileNumber`]:
            editedCompanyPhone || applicantInfo.mobileNo,
          [`prospect.signatoryInfo[${currentStateSignatoryIndex}].contactDetails.editedPrimaryEmailId`]:
            editedStakeholderEmail ||
            signatoryDetails[currentStateSignatoryIndex]?.contactDetails?.primaryEmail,
          [`prospect.signatoryInfo[${currentStateSignatoryIndex}].contactDetails.editedPrimaryMobileNo`]:
            editedStakeholderPhone ||
            signatoryDetails[currentStateSignatoryIndex]?.contactDetails?.primaryMobileNo
        })
      );
    }
  } catch (error) {
    log(error);
    let message = error?.response?.data?.message;
    const errorDebugMsg = error?.response?.data?.debugMessage;
    if (errorDebugMsg) {
      const clarityErrorFields = [
        "PersonalDetails.Gender.Code",
        "PersonalDetails.PlaceOfBirth",
        "PersonalDetails.BirthDate",
        "PassportDetails.PassportNumber",
        "PassportDetails.ExpiryDate",
        "IdentityCardDetails.RegistrantNumber",
        "IdentityCardDetails.ExpiryDate",
        "IdentityCardDetails.IssueDate"
      ];

      const errorExists = clarityErrorFields.map(field =>
        field.toLowerCase().includes(errorDebugMsg?.toLowerCase())
      );

      if (errorExists) {
        yield put(
          setScreeningError({
            error: "MOI Data Check",
            icon: "",
            screeningType: 403,
            isEFRDeclinedScreen: true
          })
        );
        yield put(notifyHostError(message));
        yield call(sendProspectToApiWithScreeningResults, EFR_CHECK);
      }
    } else {
      const notificationOptions = { title: "Oops", message: error.message };
      NotificationsManager.add(notificationOptions);
      yield put(notifyHostError(message));
    }
  }
}

export function* showScreeningError(status) {
  let screenError = screeningStatus.find(({ screeningType }) => screeningType === status);
  let buttons = [
    {
      external: false,
      link: routes.quickapplyLanding,
      label: "Back to home"
    }
  ];
  if (buttons !== undefined) {
    screenError = { ...screenError, ...buttons };
  }

  yield put(
    setScreeningError({
      ...screenError,
      text: EFR_CHECK_ERROR,
      icon: "",
      screeningType: 403,
      isEFRDeclinedScreen: true
    })
  );
}

export function* entityConfirmation(tlia) {
  const { licenseOrCOINumber, licenseOrCOIExpiryDate, dateOfIncorporation } = yield select(
    getOrganizationInfo
  );

  const data = {
    tradeLicenseNumber: licenseOrCOINumber,
    expiryDate: licenseOrCOIExpiryDate,
    creationDate: dateOfIncorporation
  };

  try {
    let headers = yield select(getAuthorizationHeader);
    const livelinessData = yield select(getLivelinessData);
    const transactionId = yield select(getTransactionId);
    yield put(resetFormStep(true));
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);

    headers = {
      headers: {
        ...headers.headers,
        "X-Recaptcha-Token":
          "03AGdBq25uLcqQmoufID-aCqfzV_wEOYl63bo92SDoA6R8NGE-FFkhlREI28Akm04ryxnbY29ydjyYumT5xBqllUXODFW_GSlSOhJZd43CjLhyBlsVDk1GzHs_aFeYfwvBRkkRgMqW-CLJ9VCOLgqyYy7KDhcmkg-Ox0gPIjgD8KGIBWbhekzGIiuawj_XD4cEkzEAJy5F_rtFzUt_C48HgptUfLq5CWjyPGxWBqWSWyXaKEuTHKTl1LGoCG0CF8yL6PG0Gtqi7KRk2i1K7LTw7UxOYu9GcxIj2qfsMj2ya_NdYQAdmIrlq4LjYOP49nKp5EbXzhDG0JQH6Pb8kFCt_S2MCI3u86Zyu4zqZv1ZturlDajTKoze7MaQUjCbXHTiqMsUcUCnq22LeLzHTcZ_FcEsAVbjsUZSM0gQk8q4M7uBNzQdF9ZO9Vi5y40fBtkJCiObo2sh82yL07tfuXpQpN7JYBqXEKGEOA"
      }
    };

    yield call(
      analyzeOcrData.entityConfirmation,
      transactionId,
      livelinessData.data,
      livelinessData.datahash,
      { ...data, issuingAuthority: parseInt(tlia.displayText) },
      headers,
      webMobileRefId
    );
    yield put(
      validateEntityConfirmSuccess({
        success: true,
        issuingAuthority: tlia.code,
        ...data
      })
    );
  } catch (error) {
    let message = error?.response?.data?.message;
    if (error?.response?.status === 403 && error?.response?.data?.errorCode === "024") {
      yield call(showScreeningError, EFR_CHECK);
      yield put(
        validateEntityConfirmSuccess({ success: true, ...data, issuingAuthority: tlia.code })
      );
    } else {
      yield put(validateEntityConfirmFail(null));
      const notificationOptions = { title: "Oops", message };
      NotificationsManager.add(notificationOptions);
      log(error);
    }
  } finally {
    yield put(resetFormStep(false));
  }
}

export function* setLivelinessData({ payload }) {
  try {
    const livelinessData = yield select(getLivelinessData);
    const transactionId = yield select(getTransactionId);
    let headers = yield select(getAuthorizationHeader);
    headers = {
      headers: {
        ...headers.headers,
        "X-Recaptcha-Token":
          "03AGdBq25uLcqQmoufID-aCqfzV_wEOYl63bo92SDoA6R8NGE-FFkhlREI28Akm04ryxnbY29ydjyYumT5xBqllUXODFW_GSlSOhJZd43CjLhyBlsVDk1GzHs_aFeYfwvBRkkRgMqW-CLJ9VCOLgqyYy7KDhcmkg-Ox0gPIjgD8KGIBWbhekzGIiuawj_XD4cEkzEAJy5F_rtFzUt_C48HgptUfLq5CWjyPGxWBqWSWyXaKEuTHKTl1LGoCG0CF8yL6PG0Gtqi7KRk2i1K7LTw7UxOYu9GcxIj2qfsMj2ya_NdYQAdmIrlq4LjYOP49nKp5EbXzhDG0JQH6Pb8kFCt_S2MCI3u86Zyu4zqZv1ZturlDajTKoze7MaQUjCbXHTiqMsUcUCnq22LeLzHTcZ_FcEsAVbjsUZSM0gQk8q4M7uBNzQdF9ZO9Vi5y40fBtkJCiObo2sh82yL07tfuXpQpN7JYBqXEKGEOA"
      }
    };
    const { licenseIssuingAuthority } = yield select(getOrganizationInfo);
    const currentStateSignatoryIndex = yield select(getSelectedSignatoryIndex);
    const { tliaForMOI } = yield select(getDatalist);
    const filteredlicenseIssuingAuthority = tliaForMOI.filter(
      tlia => tlia?.value === licenseIssuingAuthority
    );
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);

    yield call(
      analyzeOcrData.validateAndConfirmIdentity,
      transactionId,
      livelinessData.data,
      livelinessData.datahash,
      headers,
      webMobileRefId
    );
    const acceptedTimeStamp = formattedAccTimeStamp(new Date());
    // adding this to clear already saved (if any) editedfullname
    yield put(
      updateProspect({
        [`prospect.signatoryInfo[${currentStateSignatoryIndex}].editedFullName`]: "",
        [`prospect.signatoryInfo[${currentStateSignatoryIndex}].eFRCompletedTimestamp`]: acceptedTimeStamp
      })
    );

    yield put(sendProspectToAPI(AUTO));

    yield put(validateIdentitySuccess());
    if (filteredlicenseIssuingAuthority?.length) {
      yield call(entityConfirmation, filteredlicenseIssuingAuthority[0], headers);
    } else {
      yield put(
        validateEntityConfirmSuccess({
          success: true
        })
      );
    }
  } catch (error) {
    if (error?.response?.status === 403 && error?.response?.data?.errorCode === "017") {
      yield call(sendProspectToApiWithScreeningResults, EFR_CHECK);
    }
    let message = error?.response?.data?.message;
    yield put(validateIdentityFail(message));
    log(error);
  }
}

function* sendProspectToApiWithScreeningResults() {
  const signatoryId = yield select(getSelectedStakeholderId);
  const isMultiPartnerJourney = yield select(getIsMultiPartnerJourney);
  const individualId = isMultiPartnerJourney && signatoryId ? signatoryId : defaultSignatoryId;
  const currentStateSignatoryIndex = yield select(getSelectedSignatoryIndex);
  yield put(
    updateProspect({
      [`prospect.signatoryInfo[${currentStateSignatoryIndex}].isEFRCheckLimitExceeded`]: true,
      [`prospect.signatoryInfo[${currentStateSignatoryIndex}].signatoryId`]: individualId
    })
  );
  yield put(sendProspectToAPI(NEXT));
  yield take(action => {
    // Wait for any action that indicates sendProspectToAPI has completed
    return (
      action.type.includes(SEND_PROSPECT_TO_API_SUCCESS) ||
      action.type.includes(SEND_PROSPECT_TO_API_FAIL)
    );
  });
  yield call(showScreeningError, EFR_CHECK);
}

function* getEidDocuments(transactionId, headers) {
  try {
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);

    return yield call(
      getOCRDataStatus.getOCRStageData,
      transactionId,
      "EID_OCR",
      headers,
      webMobileRefId
    );
  } catch (error) {
    log(error);
  }
}

function* getPassportDocuments(transactionId, headers) {
  try {
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);

    return yield call(
      getOCRDataStatus.getOCRStageData,
      transactionId,
      "PASSPORT_OCR",
      headers,
      webMobileRefId
    );
  } catch (error) {
    log(error);
  }
}

function* getConfirmEntityStatus(transactionId, headers) {
  try {
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);

    return yield call(
      getOCRDataStatus.getOCRStageData,
      transactionId,
      "CONFIRM_ENTITY",
      headers,
      webMobileRefId
    );
  } catch (error) {
    log(error);
  }
}

const extractOCRData = payload => {
  let eidFront = payload.documentDetails.find(eachDoc => eachDoc.documentType === "EID_FRONT")
    ?.documentContent;
  let eidBack = payload.documentDetails.find(eachDoc => eachDoc.documentType === "EID_BACK")
    ?.documentContent;
  let passport = payload.documentDetails.find(eachDoc => eachDoc.documentType === "PASSPORT_FRONT")
    ?.documentContent;
  return {
    efrResponse: payload.efrResponse,
    eidFront,
    eidBack,
    passport
  };
};

function* putOcrData(transactionId, headers) {
  const eidDocuments = yield call(getEidDocuments, transactionId, headers);
  const passportDocuments = yield call(getPassportDocuments, transactionId, headers);
  const eidOcrDetails = extractOCRData(eidDocuments);
  const passportDetails = extractOCRData(passportDocuments);
  yield put(loadEidDocuments(eidOcrDetails));
  const isPassportValidated = yield call(
    validateOCR,
    passportDetails?.efrResponse,
    DOC_TYPE_PASSPORT,
    eidOcrDetails.efrResponse
  );
  if (isPassportValidated) {
    yield put(loadPassportDocuments(passportDetails));
  }
}

export function* getCurrentKYCStatus() {
  try {
    const transactionId = yield select(getTransactionId);
    const headers = yield select(getAuthorizationHeader);
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);

    const stagesResponse = yield call(
      getOCRDataStatus.getOCRStatus,
      transactionId,
      headers,
      webMobileRefId
    );
    const {
      licenseIssuingAuthority,
      licenseOrCOINumber,
      licenseOrCOIExpiryDate,
      dateOfIncorporation
    } = yield select(getOrganizationInfo);
    const { tliaForMOI } = yield select(getDatalist);

    const foundLicenseIssuingAuthority = tliaForMOI.find(
      tlia => tlia?.value === licenseIssuingAuthority
    );

    const data = {
      tradeLicenseNumber: licenseOrCOINumber,
      expiryDate: licenseOrCOIExpiryDate,
      creationDate: dateOfIncorporation,
      issuingAuthority: licenseIssuingAuthority
    };

    const stageInfo = stagesResponse.stageInfo;
    let stageInfoMap = {};
    let isLimitExceeded = false;
    stageInfo.forEach(eachStage => {
      stageInfoMap[eachStage.stage] = eachStage.isCompleted;
      if (eachStage.isLimitExceeded) {
        isLimitExceeded = true;
      }
    });
    if (isLimitExceeded) {
      yield call(showScreeningError, EFR_CHECK);
      return;
    }
    const checkEntitySuccess = inputData => {
      if (!inputData) {
        return false;
      }
      const { tradeLicenseNumber, issuingAuthority, expiryDate, creationDate } = inputData;
      const issuingAuthorityCode = tliaForMOI.find(tlia => tlia?.displayText === issuingAuthority)
        ?.code;
      if (
        tradeLicenseNumber !== data.tradeLicenseNumber ||
        expiryDate !== data.expiryDate ||
        creationDate !== data.creationDate ||
        issuingAuthorityCode !== data.issuingAuthority
      ) {
        return false;
      }
      return true;
    };
    if (
      !foundLicenseIssuingAuthority &&
      stageInfoMap["CONFIRM_DATA_ELEMENT"] &&
      !stageInfoMap["CONFIRM_ENTITY"]
    ) {
      yield call(putOcrData, transactionId, headers);
      const moiIssuingAuthority = tliaForMOI.find(tlia => tlia?.code === data.issuingAuthority)
        ?.displayText;
      if (!moiIssuingAuthority) {
        yield put(loadConfirmEntity({ success: true, ...data }));
      }
    } else if (stageInfoMap["CONFIRM_ENTITY"]) {
      yield call(putOcrData, transactionId, headers);
      const response = yield call(getConfirmEntityStatus, transactionId, headers);
      const isEntitySuccess = checkEntitySuccess(response.inputData);
      if (isEntitySuccess) {
        yield put(loadConfirmEntity({ success: true, ...data }));
      }
    } else if (stageInfoMap["PASSPORT_OCR"]) {
      yield call(putOcrData, transactionId, headers);
    } else if (stageInfoMap["EID_OCR"]) {
      const eidDocuments = yield call(getEidDocuments, transactionId, headers);
      const eidOcrDetails = extractOCRData(eidDocuments);
      const isDocValid = checkDocumentValid(eidOcrDetails?.efrResponse);
      if (isDocValid) {
        const isEidValidated = yield call(validateOCR, eidOcrDetails?.efrResponse, DOC_TYPE_EID);
        if (isEidValidated) {
          yield put(loadEidDocuments(eidOcrDetails));
        }
      }
    }
    yield put(getKycSuccess(true));
    yield put(setLoading(false));
  } catch (error) {
    yield put(getKycError(error));
    log(error);
  }
}

function* sendEfrInvite({ payload }) {
  try {
    const roCode = yield select(getRoCode);
    const roAgentId = yield select(getAgentId);
    const headers = yield select(getAuthorizationHeader);
    const prospectId = yield select(getProspectId);
    const body = {
      action: "generate",
      roCode,
      roAgentId
    };
    const pId = prospectId || payload;
    yield call(sendEFRInvite.sendMail, pId, body, headers);
    yield put(sendEFRInviteSuccess(true));
  } catch (error) {
    yield put(sendEFRInviteError(false));
    log(error);
  }
}

export default function* KycTransactionSaga() {
  yield all([
    takeLatest(CREATE_KYC_TRANSACTION, createKycTransactionSaga),
    takeLatest(ANALYSE_OCR, analyseOcrDataSaga),
    takeLatest(CREATE_FACE_SCAN_KEY, createFaceScanSaga),
    takeLatest(CHECK_FACE_LIVELINESS, checkFaceLiveliness),
    takeLatest(SET_LIVELINESS_DATA, setLivelinessData),
    takeLatest(NOTIFY_HOST, notifyHost),
    takeLatest(GET_KYC_STATUS, getCurrentKYCStatus),
    takeLatest(SEND_EFR_INVITE, sendEfrInvite)
  ]);
}
